import {
  formatMinor,
  fromMoneyDto,
  minorToDecimalString,
  parseMinorUnits,
  toMoneyDto,
  type FormatMoneyOptions,
  type MoneyDto,
} from '@robu/shared';

export { formatMinor, fromMoneyDto, minorToDecimalString, parseMinorUnits, toMoneyDto };
export type { FormatMoneyOptions, MoneyDto };

/**
 * Money never becomes a `number` in this app (TZ §17). Everything below works
 * on `bigint` minor units and only crosses into strings for rendering.
 */

export const ZERO_MONEY: MoneyDto = { minor: '0', decimal: '0.00' };

export function minorOf(dto: MoneyDto | null | undefined): bigint {
  if (!dto) return 0n;
  return fromMoneyDto(dto);
}

export function formatMoney(
  dto: MoneyDto | null | undefined,
  options?: FormatMoneyOptions,
): string {
  return formatMinor(minorOf(dto), { currencySymbol: '', ...options });
}

/** Ledger rows read better when credits carry an explicit "+". */
export function formatSignedMoney(dto: MoneyDto | null | undefined): string {
  return formatMinor(minorOf(dto), { currencySymbol: '', alwaysShowSign: true });
}

export function isZeroMoney(dto: MoneyDto | null | undefined): boolean {
  return minorOf(dto) === 0n;
}

export function isNegativeMoney(dto: MoneyDto | null | undefined): boolean {
  return minorOf(dto) < 0n;
}

export function sumMoney(values: readonly (MoneyDto | null | undefined)[]): bigint {
  return values.reduce<bigint>((total, value) => total + minorOf(value), 0n);
}

/**
 * Validates an operator-entered amount for the admin balance forms. Returns the
 * parsed minor units so the caller can compare against a balance without ever
 * touching floating point.
 */
export function tryParseAmount(input: string): { ok: true; minor: bigint } | { ok: false } {
  try {
    const minor = parseMinorUnits(input.trim());
    return { ok: true, minor };
  } catch {
    return { ok: false };
  }
}

/** Percent strings arrive as decimals ("10.00"); render them without noise. */
export function formatPercent(percent: string): string {
  const trimmed = percent.replace(/\.?0+$/, '');
  return `${(trimmed === '' ? '0' : trimmed).replace('.', ',')} %`;
}
